import { Fragment, useEffect, useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { Panel } from '../components/ui/Panel';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { SectionHeader } from '../components/ui/SectionHeader';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Plus, Copy, CheckCircle2, Gift, Trash2, Pencil, Network, Info, AlertTriangle } from 'lucide-react';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { copyToClipboard } from '../lib/utils';
import { ApiError } from '../lib/api';
import type { AffiliateNetwork } from './AffiliateNetworks';
import type { ReactNode } from 'react';

interface ByOfferStat {
  offerId: number;
  clicksTotal: number;
  clicksQuality: number;
  conversionsApproved: number;
  revenueGross: number;
}

// Param postback EFEKTIF dari API (offer > network > default generik).
interface EffectiveParams {
  postbackParamClickId: string;
  postbackParamRevenue: string;
  postbackParamStatus: string;
  postbackParamSubId: string | null;
  postbackStatusApproved: string;
  postbackStatusRejected: string;
  postbackAuthType: 'none' | 'hmac' | 'token' | 'ip_whitelist';
}

interface Offer {
  id: number;
  name: string;
  networkId: number | null;
  networkName: string | null;
  templateUrl: string;
  status: string;
  postbackUrl: string | null;
  postbackAuthType: 'none' | 'hmac' | 'token' | 'ip_whitelist';
  postbackAuthValue: string | null;
  postbackParamClickId: string;
  postbackParamRevenue: string;
  postbackParamStatus: string;
  postbackParamSubId: string | null;
  postbackStatusApproved: string;
  postbackStatusRejected: string;
  effectiveParams?: EffectiveParams;
}

// Postback host used to render copy-paste URLs in the UI. Override per build via
// VITE_POSTBACK_HOST so swapping the postback worker domain doesn't need a code edit.
const POSTBACK_HOST = (import.meta.env.VITE_POSTBACK_HOST as string | undefined)?.replace(/\/+$/, '')
  ?? 'https://rj-postback.jelitanadea77.workers.dev';

// Environment-based POSTBACK_BASE_URL for the strip pattern display
const POSTBACK_BASE_URL = import.meta.env.VITE_POSTBACK_BASE_URL ?? '';

function buildPostbackUrl(o: Offer): string {
  const p = o.effectiveParams;
  const cid = p?.postbackParamClickId || o.postbackParamClickId || 'click_id';
  const rev = p?.postbackParamRevenue || o.postbackParamRevenue || 'revenue';
  const st = p?.postbackParamStatus || o.postbackParamStatus || 'status';
  const sub = (p?.postbackParamSubId ?? o.postbackParamSubId ?? 'sub_id').trim() || 'sub_id';
  return (
    `${POSTBACK_HOST}/pb/${o.id}` +
    `?${cid}={${cid}}` +
    `&${sub}={${sub}}` +
    `&${rev}={${rev}}` +
    `&${st}={${st}}` +
    `&offer_name={offer_name}` +
    `&goal={goal}`
  );
}

export function OffersPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Offer | null>(null);
  const [selectedNetwork, setSelectedNetwork] = useState<AffiliateNetwork | null>(null);

  const list = useQuery({
    queryKey: ['offers'],
    queryFn: () => apiClient.get<Offer[]>('/api/v1/offers'),
  });

  // Fetch real-time EPC24 stats from D1+AE
  const offersStats = useQuery({
    queryKey: ['by-offer-stats', 'today'],
    queryFn: () => apiClient.get<{ data: ByOfferStat[] }>('/api/v1/stats/by-offer?range=today&limit=200'),
  });

  // Build offerId → stat map for EPC24 lookup
  const offerStatsMap = useMemo(() => {
    const map = new Map<number, ByOfferStat>();
    for (const stat of offersStats.data?.data ?? []) {
      map.set(stat.offerId, stat);
    }
    return map;
  }, [offersStats.data]);

  // Load networks for lookup and group building
  const networksList = useQuery({
    queryKey: ['affiliate-networks'],
    queryFn: () => apiClient.get<AffiliateNetwork[]>('/api/v1/networks'),
  });

  // Build network name → count mapping from offers (no new endpoint needed)
  const networkStats = useMemo(() => {
    const stats = new Map<string, { count: number; offers: Offer[] }>();
    for (const offer of list.data ?? []) {
      const netName = offer.networkName || 'Tanpa Network';
      const existing = stats.get(netName) || { count: 0, offers: [] };
      existing.count += 1;
      existing.offers.push(offer);
      stats.set(netName, existing);
    }
    return stats;
  }, [list.data]);

  // Sort networks by count desc, then name asc
  const sortedNetworks = useMemo(() => {
    return Array.from(networkStats.entries()).sort((a, b) => {
      if (b[1].count !== a[1].count) return b[1].count - a[1].count;
      return a[0].localeCompare(b[0]);
    });
  }, [networkStats]);

  const create = useMutation({
    mutationFn: (data: any) => apiClient.post('/api/v1/offers', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['offers'] });
      setCreateOpen(false);
    },
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      apiClient.patch(`/api/v1/offers/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['offers'] });
      setEditTarget(null);
    },
  });

  const archive = useMutation({
    mutationFn: ({ id, hard }: { id: number; hard?: boolean }) =>
      apiClient.delete(`/api/v1/offers/${id}${hard ? '?hard=true' : ''}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['offers'] }),
  });

  // Delete UX: default = hard-delete; bila API 409 OFFER_IN_USE (offer masih
  // dipakai routing rule — proteksi agar routing live tidak mati diam-diam),
  // fallback ke archive (soft-delete) supaya klik tetap ada dan user tahu
  // kenapa delete "tidak jalan".
  const handleDelete = async (offer: { id: number; name: string }) => {
    const result = await confirm({
      title: `Delete ${offer.name}?`,
      description:
        'Hard-delete menghapus permanen (ditolak bila masih dipakai rule routing). Archive menyembunyikan offer tanpa menghapus data.',
      variant: 'danger',
      actions: [
        { label: 'Archive', variant: 'neon', result: 'archive' },
        { label: 'Delete Permanen', variant: 'danger', result: 'delete', icon: <Trash2 size={14} /> },
      ],
    });
    if (result === 'archive') {
      archive.mutate({ id: offer.id });
      return;
    }
    if (result !== 'delete') return;
    try {
      await apiClient.delete(`/api/v1/offers/${offer.id}?hard=true`);
      qc.invalidateQueries({ queryKey: ['offers'] });
    } catch (e) {
      if (e instanceof ApiError && (e.code === 'OFFER_IN_USE' || e.status === 409)) {
        const fallback = await confirm({
          title: 'Offer masih dipakai rule routing',
          description: `${e.message} — archive offer ini sebagai gantinya?`,
          variant: 'warning',
          confirmLabel: 'Archive saja',
        });
        if (fallback !== null) archive.mutate({ id: offer.id });
      } else {
        alert(`Delete gagal: ${e instanceof ApiError ? e.message : 'unknown error'}`);
      }
    }
  };

  // Group offers by network for left table
  const groupedOffers = useMemo(() => {
    const groups = new Map<string, Offer[]>();
    for (const offer of list.data ?? []) {
      const netName = offer.networkName || 'Tanpa Network';
      const existing = groups.get(netName) || [];
      existing.push(offer);
      groups.set(netName, existing);
    }
    return groups;
  }, [list.data]);

  const sortedGroupKeys = useMemo(() => {
    return Array.from(groupedOffers.keys()).sort((a, b) => a.localeCompare(b));
  }, [groupedOffers]);

  return (
    <div className="w-full space-y-5 sm:space-y-6 animate-fade-in">
      {/* Header */}
      <SectionHeader
        title="Offers"
        subtitle={`${list.data?.length ?? 0} total offers • ${sortedGroupKeys.length} networks`}
        actions={
          <Button
            variant="neon"
            icon={<Plus size={14} />}
            onClick={() => setCreateOpen(true)}
          >
            + NEW OFFER
          </Button>
        }
      />

      {/* Main split 7/5 */}
      <div className="grid grid-cols-1 lg:grid-cols-[7fr_5fr] gap-4 sm:gap-6">
        {/* LEFT: Offers Table (7fr) */}
        <Panel level={1}>
          <div className="p-4 border-b border-[rgba(0,255,225,0.18)]">
            <h2 className="font-display text-lg font-bold uppercase tracking-wider text-neon-cyan">
              Offers
            </h2>
          </div>
          <div className="overflow-x-auto max-h-[calc(100vh-280px)] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-bg-primary sticky top-0 z-10">
                <tr>
                  <th className="text-left py-2 px-3 font-display text-[10px] uppercase tracking-widest text-neon-cyan border-b border-[rgba(0,255,225,0.18)]">Offer</th>
                  <th className="text-left py-2 px-3 font-display text-[10px] uppercase tracking-widest text-neon-cyan border-b border-[rgba(0,255,225,0.18)]">Network</th>
                  <th className="text-left py-2 px-3 font-display text-[10px] uppercase tracking-widest text-neon-cyan border-b border-[rgba(0,255,225,0.18)]">Payout</th>
                  <th className="text-left py-2 px-3 font-display text-[10px] uppercase tracking-widest text-neon-cyan border-b border-[rgba(0,255,225,0.18)]">Postback</th>
                  <th className="text-left py-2 px-3 font-display text-[10px] uppercase tracking-widest text-neon-cyan border-b border-[rgba(0,255,225,0.18)]">Status</th>
                  <th className="text-right py-2 px-3 font-display text-[10px] uppercase tracking-widest text-neon-cyan border-b border-[rgba(0,255,225,0.18)]">EPC24</th>
                  <th className="text-right py-2 px-3 font-display text-[10px] uppercase tracking-widest text-neon-cyan border-b border-[rgba(0,255,225,0.18)]">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgba(0,255,225,0.08)]">
                {sortedGroupKeys.map((netName, groupIdx) => {
                  const offers = groupedOffers.get(netName) ?? [];
                  // Dialek postback network (Stitch layar 4: chips di group
                  // header — param klik + kode status + auth). Sumber = tabel
                  // affiliate_networks; "Tanpa Network" tak punya dialek.
                  const net = networksList.data?.find((n) => n.name === netName);
                  return (
                    <Fragment key={netName}>
                      {/* Network group header */}
                      <tr className="bg-bg-secondary">
                        <td colSpan={7} className="py-2 px-3">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <span className="font-mono text-xs text-neon-cyan">
                              <NetworkIcon className="inline -mt-0.5 mr-1.5" /> {netName}
                              <span className="ml-3 text-[10px] text-text-tertiary">
                                ({offers.length} offers)
                              </span>
                            </span>
                            {net && (
                              <span className="flex flex-wrap items-center gap-1">
                                <code className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm border border-neon-cyan/30 text-neon-cyan/90 bg-bg-tertiary" title="param click_id network">
                                  {net.postbackParamClickId}=&#123;{net.postbackParamClickId}&#125;
                                </code>
                                <code className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm border border-neon-green/30 text-neon-green/90 bg-bg-tertiary" title="kode status approved / rejected">
                                  ok={net.postbackStatusApproved} · rej={net.postbackStatusRejected}
                                </code>
                                <span
                                  className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm border ${
                                    net.postbackAuthType === 'none'
                                      ? 'border-border-subtle text-text-tertiary'
                                      : 'border-neon-purple/40 text-neon-purple'
                                  }`}
                                  title="autentikasi postback network"
                                >
                                  auth:{net.postbackAuthType}
                                </span>
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                      {/* Offer rows */}
                      {offers.map((offer) => {
                        const stat = offerStatsMap.get(offer.id);
                        const epc24 = stat ? (stat.revenueGross / stat.clicksQuality) : undefined;
                        return (
                          <OfferRow
                            key={offer.id}
                            offer={offer}
                            onDelete={handleDelete}
                            onEdit={(o) => setEditTarget(o)}
                            epc24={epc24}
                          />
                        );
                      })}
                    </Fragment>
                  );
                })}
                {(list.data?.length ?? 0) === 0 && !list.isLoading && (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-text-tertiary">
                      <NoOffersIcon className="mx-auto mb-2 text-text-tertiary" size={32} />
                      <p className="font-mono text-xs">no offers yet — add one to start routing traffic</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>

        {/* RIGHT STACKED PANELS (5fr) */}
        <div className="w-full space-y-4 sm:space-y-5 flex flex-col">
          {/* AFFILIATE NETWORKS panel */}
          <Panel level={1} bracket>
          <div className="p-4 border-b border-[rgba(0,255,225,0.18)]">
            <h2 className="font-display text-lg font-bold uppercase tracking-wider text-neon-cyan">
              Affiliate Networks
            </h2>
          </div>
            <div className="p-3 space-y-2 max-h-[300px] overflow-y-auto">
              {sortedNetworks.map(([netName, stats]) => (
                <button
                  key={netName}
                  onClick={() => setSelectedNetwork(
                    networksList.data?.find(n => n.name === netName) || null
                  )}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded transition-colors ${
                    selectedNetwork?.name === netName
                      ? 'bg-neon-cyan/20 border border-neon-cyan/40'
                      : 'hover:bg-bg-tertiary'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-neon-cyan/20">
                      <NetworkIcon size={12} className="text-neon-cyan" />
                    </span>
                    <span className={`font-mono text-xs ${selectedNetwork?.name === netName ? 'text-neon-cyan' : 'text-text-primary'}`}>
                      {netName}
                    </span>
                  </div>
                  <span className="font-mono text-[10px] text-text-tertiary">{stats.count} linked</span>
                </button>
              ))}
              {sortedNetworks.length === 0 && (
                <div className="text-center py-8 text-text-tertiary text-xs font-mono">
                  belum ada affiliate network —<br />
                  daftarkan di menu <a href="/networks" className="text-neon-cyan underline">Affiliate Networks</a>
                </div>
              )}
            </div>
            <div className="p-3 border-t border-[rgba(0,255,225,0.18)]">
              <a
                href="/networks"
                className="block w-full text-center py-1.5 text-[11px] font-display uppercase tracking-wider rounded border border-neon-cyan/30 text-neon-cyan hover:bg-neon-cyan/10 transition-colors"
              >
                Kelola Networks →
              </a>
            </div>
          </Panel>

          {/* NETWORK DIALECT PREVIEW panel */}
          <Panel level={2} bracket>
            <div className="p-4 border-b border-[rgba(0,255,225,0.18)]">
              <h2 className="font-display text-lg font-bold uppercase tracking-wider text-neon-magenta">
                Network Dialect Preview
              </h2>
            </div>
            <div className="p-4">
              {selectedNetwork ? (
                <DialectPreview network={selectedNetwork} />
              ) : (
                <div className="font-mono text-[10px] text-text-tertiary text-center py-6">
                  select a network<br />
                  to preview dialect
                </div>
              )}
            </div>
          </Panel>
        </div>
      </div>

      {/* BOTTOM STRIP: Postback URL Pattern */}
      <div className="font-mono text-[10px] text-text-tertiary bg-bg-tertiary border border-[rgba(0,255,225,0.18)] rounded p-3 overflow-x-auto">
        <span className="text-text-secondary">POSTBACK_PATTERN:</span>{' '}
        <span className="text-neon-green">
          {POSTBACK_BASE_URL || 'https://rj-postback.jelitanadea77.workers.dev'}/pb/{'<offer_id>'}
          ?{'<click_id>'}={'{'}{':click_id'}{'}'}
          &amp;{'<sub_id>'}={'{'}{':sub_id'}{'}'}
          &amp;{'<revenue>'}={'{'}{':revenue'}{'}'}
          &amp;{'<status>'}={'{'}{':status'}{'}'}
        </span>
      </div>

      {/* Modals */}
      {createOpen && (
        <OfferFormModal
          open={createOpen}
          mode="create"
          onClose={() => setCreateOpen(false)}
          onSubmit={(d) => create.mutate(d)}
          loading={create.isPending}
        />
      )}
      {editTarget !== null && (
        <OfferFormModal
          open={true}
          mode="edit"
          offer={editTarget}
          onClose={() => setEditTarget(null)}
          onSubmit={(d) => editTarget && update.mutate({ id: editTarget.id, data: d })}
          loading={update.isPending}
        />
      )}
    </div>
  );
}

// ─── Single Offer Row Component ────────────────────────────────────────────────

interface OfferRowProps {
  offer: Offer;
  onDelete: (offer: { id: number; name: string }) => void;
  onEdit: (offer: Offer) => void;
  epc24?: number | null;
}

function OfferRow({ offer, onDelete, onEdit, epc24 }: OfferRowProps) {
  const postbackUrl = buildPostbackUrl(offer);
  const netName = offer.networkName ?? undefined;
  const hasPostback = offer.postbackUrl && offer.postbackUrl.trim().length > 0;

  // Mock EPC24 values — real impl would fetch from D1/AE analytics
  const epcValue = epc24 ?? undefined;

  return (
    <tr className="hover:bg-bg-secondary/50 transition-colors">
      <td className="py-2 px-3">
        <div className="font-display font-semibold text-sm text-text-primary truncate">
          {offer.name}
        </div>
        <div className="text-[10px] text-text-tertiary font-mono">#{offer.id}</div>
      </td>
      <td className="py-2 px-3">
        {netName ? (
          <span className="font-mono text-xs text-neon-cyan">{netName}</span>
        ) : (
          <span className="text-text-tertiary text-xs">—</span>
        )}
      </td>
      <td className="py-2 px-3">
        <span className="font-mono text-xs text-neon-yellow">$0.00 CPA</span>
      </td>
      <td className="py-2 px-3">
        {hasPostback ? (
          <span className="inline-flex items-center gap-1 text-neon-green text-xs font-mono">
            <CheckCircle2 size={12} /> ✓
          </span>
        ) : (
          <span className="text-text-tertiary text-xs">-</span>
        )}
      </td>
      <td className="py-2 px-3">
        <StatusBadge status={offer.status} />
      </td>
      <td className="py-2 px-3 text-right">
        <span className="font-mono text-xs text-neon-cyan">
          {epcValue !== undefined ? `$${epcValue.toFixed(4)}` : '—'}
        </span>
      </td>
      <td className="py-2 px-3 text-right">
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            icon={<EditIcon size={12} />}
            onClick={() => onEdit(offer)}
            title="Edit offer"
          />
          <Button
            variant="ghost"
            size="sm"
            icon={<DeleteIcon size={12} />}
            onClick={() => onDelete({ id: offer.id, name: offer.name })}
            title="Delete"
          />
        </div>
      </td>
    </tr>
  );
}

// ─── Network Dialect Preview Component ─────────────────────────────────────────

function DialectPreview({ network }: { network: AffiliateNetwork }) {
  return (
    <div className="w-full space-y-2">
      {/* Click Mapping */}
      <div>
        <h3 className="font-display text-xs font-bold uppercase tracking-widest text-neon-magenta mb-2">
          Click Params
        </h3>
        <div className="font-mono text-[10px] space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-text-secondary">click_id</span>
            <span className="text-neon-cyan">=</span>
            <code className="bg-bg-tertiary px-2 py-0.5 rounded text-neon-yellow">{network.postbackParamClickId}</code>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-text-secondary">sub_id</span>
            <span className="text-neon-cyan">=</span>
            <code className="bg-bg-tertiary px-2 py-0.5 rounded text-neon-yellow">{network.postbackParamSubId || 'sub_id'}</code>
          </div>
        </div>
      </div>

      {/* Revenue Mapping */}
      <div>
        <h3 className="font-display text-xs font-bold uppercase tracking-widest text-neon-magenta mb-2">
          Revenue Params
        </h3>
        <div className="font-mono text-[10px] space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-text-secondary">revenue</span>
            <span className="text-neon-cyan">=</span>
            <code className="bg-bg-tertiary px-2 py-0.5 rounded text-neon-yellow">{network.postbackParamRevenue}</code>
          </div>
        </div>
      </div>

      {/* Status Mapping */}
      <div>
        <h3 className="font-display text-xs font-bold uppercase tracking-widest text-neon-magenta mb-2">
          Status Values
        </h3>
        <div className="font-mono text-[10px] space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-neon-green">approved</span>
            <span className="text-text-secondary">→</span>
            <code className="bg-bg-tertiary px-2 py-0.5 rounded text-neon-yellow">{network.postbackStatusApproved}</code>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-neon-red">rejected</span>
            <span className="text-text-secondary">→</span>
            <code className="bg-bg-tertiary px-2 py-0.5 rounded text-neon-yellow">{network.postbackStatusRejected}</code>
          </div>
          {network.postbackStatusPending && (
            <div className="flex items-center gap-2">
              <span className="text-neon-yellow">pending</span>
              <span className="text-text-secondary">→</span>
              <code className="bg-bg-tertiary px-2 py-0.5 rounded text-neon-yellow">{network.postbackStatusPending}</code>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Icon wrappers for OfferRow ───────────────────────────────────────────────

interface SimpleIconProps {
  size?: number;
  className?: string;
}

function NetworkIcon({ size = 12, className }: SimpleIconProps) {
  return <Network size={size} className={className} />;
}

function NoOffersIcon({ size = 32, className }: SimpleIconProps) {
  return <Gift size={size} className={className} />;
}

function EditIcon({ size = 12, className }: SimpleIconProps) {
  return <Pencil size={size} className={className} />;
}

function DeleteIcon({ size = 12, className }: SimpleIconProps) {
  return <Trash2 size={size} className={className} />;
}

// ─── Offer Form Modal (existing implementation from original file) ──────────────

interface OfferFormProps {
  open: boolean;
  mode: 'create' | 'edit';
  offer?: Offer | null;
  onClose: () => void;
  onSubmit: (data: any) => void;
  loading: boolean;
}

function OfferFormModal({ open, mode, offer, onClose, onSubmit, loading }: OfferFormProps) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [networkId, setNetworkId] = useState<number | null>(null);
  const networksList = useQuery({
    queryKey: ['affiliate-networks'],
    queryFn: () => apiClient.get<AffiliateNetwork[]>('/api/v1/networks'),
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [paramClickId, setParamClickId] = useState('click_id');
  const [paramSubId, setParamSubId] = useState('sub_id');
  const [paramRevenue, setParamRevenue] = useState('revenue');
  const [paramStatus, setParamStatus] = useState('status');
  const [statusApproved, setStatusApproved] = useState('approved');
  const [statusRejected, setStatusRejected] = useState('rejected');

  const applyNetworkDialect = (n?: AffiliateNetwork | null) => {
    if (!n) return;
    setParamClickId(n.postbackParamClickId);
    setParamSubId(n.postbackParamSubId || 'sub_id');
    setParamRevenue(n.postbackParamRevenue);
    setParamStatus(n.postbackParamStatus);
    setStatusApproved(n.postbackStatusApproved);
    setStatusRejected(n.postbackStatusRejected);
  };

  useEffect(() => {
    if (open && mode === 'edit' && offer) {
      setName(offer.name);
      setUrl(offer.templateUrl);
      setNetworkId(offer.networkId);
      setParamClickId(offer.effectiveParams?.postbackParamClickId ?? offer.postbackParamClickId);
      setParamSubId(offer.effectiveParams?.postbackParamSubId ?? offer.postbackParamSubId ?? 'sub_id');
      setParamRevenue(offer.effectiveParams?.postbackParamRevenue ?? offer.postbackParamRevenue);
      setParamStatus(offer.effectiveParams?.postbackParamStatus ?? offer.postbackParamStatus);
      setStatusApproved(offer.effectiveParams?.postbackStatusApproved ?? offer.postbackStatusApproved);
      setStatusRejected(offer.effectiveParams?.postbackStatusRejected ?? offer.postbackStatusRejected);
    } else if (open && mode === 'create') {
      setName('');
      setUrl('');
      setNetworkId(null);
      setParamClickId('click_id');
      setParamSubId('sub_id');
      setParamRevenue('revenue');
      setParamStatus('status');
      setStatusApproved('approved');
      setStatusRejected('rejected');
      setShowAdvanced(false);
    }
  }, [open, mode, offer]);

  const handleNetworkChange = (raw: string) => {
    const id = Number(raw) || null;
    setNetworkId(id);
    if (mode === 'create' && id) {
      applyNetworkDialect((networksList.data ?? []).find((n) => n.id === id));
    }
  };

  const handleClose = () => {
    if (mode === 'create') {
      setName('');
      setUrl('');
      setNetworkId(null);
      setShowAdvanced(false);
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={mode === 'edit' ? `Edit Offer: ${offer?.name}` : 'Add Network Offer'}
      size="lg"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const payload: any = {
            name,
            networkId,
            templateUrl: url,
            postbackParamClickId: paramClickId,
            postbackParamSubId: paramSubId,
            postbackParamRevenue: paramRevenue,
            postbackParamStatus: paramStatus,
            postbackStatusApproved: statusApproved,
            postbackStatusRejected: statusRejected,
          };
          if (mode === 'create') {
            payload.payoutType = 'cpa';
            payload.payoutValue = 0;
            payload.postbackAuthType = 'none';
          }
          onSubmit(payload);
        }}
        className="w-full space-y-4"
      >
        {mode === 'create' && (
          <div className="px-3.5 py-2.5 border border-neon-cyan/30 bg-neon-cyan/5 rounded text-[11px] font-mono text-text-secondary leading-relaxed flex gap-2 items-start">
            <Info size={14} className="inline shrink-0 text-neon-cyan mt-0.5" /> An offer = a deal at a CPA network (iMonetizeIt, Trafee, dll). Network mengirim revenue + status via postback.
          </div>
        )}

        <Input
          label="Offer Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Trafee Smartlink US"
          required
        />

        <div>
          <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">
            Affiliate Network
          </label>
          <select
            className="input-cyber text-xs"
            value={networkId ?? ''}
            onChange={(e) => handleNetworkChange(e.target.value)}
          >
            <option value="">— tanpa network —</option>
            {(networksList.data ?? []).map((n) => (
              <option key={n.id} value={n.id}>{n.name}</option>
            ))}
          </select>
          <p className="text-[10px] text-text-tertiary mt-1 font-mono">
            Dialek postback (nama param + status value) diwarisi dari network — kelola di halaman Affiliate Networks.
          </p>
        </div>

        <div>
          <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">
            Track URL (where to redirect)
          </label>
          <textarea
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://network.com/click?aff=12345&subid={sub_id}&clickid={click_id}"
            required
            rows={3}
            className="input-cyber font-mono text-xs"
          />
          <p className="text-[10px] text-text-tertiary mt-1 font-mono">
            Macros: {'{click_id}'} {'{sub_id}'} {'{country}'} {'{ip}'} {'{device}'} {'{os}'} {'{source}'}
          </p>
        </div>

        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-2 text-[11px] font-display uppercase tracking-wider text-neon-magenta hover:text-neon-cyan transition-colors"
        >
          <span className={showAdvanced ? 'rotate-90' : ''}>▶</span>
          Advanced: Postback Param Mapping
        </button>

        {showAdvanced && (
          <div className="pl-4 border-l-2 border-neon-magenta/30 space-y-3 animate-fade-in">
            <div className="text-[10px] font-mono text-text-tertiary leading-relaxed space-y-1.5 px-3 py-2.5 border border-neon-yellow/30 bg-neon-yellow/5 rounded">
              <p className="text-neon-yellow font-bold flex items-center gap-1.5">
                <AlertTriangle size={13} className="inline shrink-0" /> Yang diisi di sini = nama param di URL postback, BUKAN macro network.
              </p>
              <p>
                Worker baca <code className="text-neon-cyan">params[nama_field_ini]</code> dari URL yang masuk.
              </p>
              <p>
                Contoh URL postback di Torazzo:<br />
                <code className="text-neon-cyan break-all">.../pb/4?<u>click_id</u>={'{p2}'}&<u>sub_id</u>={'{p1}'}&...</code>
              </p>
              <p>
                → Field di sini diisi: <code className="text-neon-green">click_id</code>, <code className="text-neon-green">sub_id</code> (bagian sebelum <code>=</code>).<br />
                ✗ Jangan isi <code className="text-neon-red line-through">{'{p2}'}</code> / <code className="text-neon-red line-through">{'{p1}'}</code> — itu macro Torazzo, bukan nama param.
              </p>
              <p>Biarkan default semua kalau pakai URL postback yang sudah di-generate (di atas).</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Input
                label="Click ID param"
                value={paramClickId}
                onChange={(e) => setParamClickId(e.target.value)}
                hint="default: click_id"
              />
              <Input
                label="Sub ID param"
                value={paramSubId}
                onChange={(e) => setParamSubId(e.target.value)}
                hint="Trafee: ext_click_id · iMonet: aff_sub"
              />
              <Input
                label="Revenue param"
                value={paramRevenue}
                onChange={(e) => setParamRevenue(e.target.value)}
                hint="Trafee: sum · default: revenue"
              />
              <Input
                label="Status param"
                value={paramStatus}
                onChange={(e) => setParamStatus(e.target.value)}
                hint="default: status"
              />
            </div>
            <p className="text-[10px] font-mono text-text-tertiary">
              Status value mapping (yang dianggap approved/rejected):
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Input
                label="Approved value"
                value={statusApproved}
                onChange={(e) => setStatusApproved(e.target.value)}
                hint="contoh: approved, 1, confirmed"
              />
              <Input
                label="Rejected value"
                value={statusRejected}
                onChange={(e) => setStatusRejected(e.target.value)}
                hint="contoh: rejected, 0, declined"
              />
            </div>
          </div>
        )}

        <div className="flex gap-2 justify-end pt-2 border-t border-border-subtle">
          <Button type="button" variant="ghost" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" loading={loading} icon={mode === 'edit' ? <Pencil size={14} /> : <Plus size={14} />}>
            {mode === 'edit' ? 'Save Changes' : 'Add Offer'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
